import { useEffect, useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { ButtonShadcn } from './ui/button-shadcn';
import { CheckCircle2 } from 'lucide-react';
import {
  PiArrowsClockwiseThin,
  PiSmileyMehFill,
  PiSmileyWinkFill,
} from 'react-icons/pi';
import { useManagerEffect } from '@/shared/managerHelpers';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import { observer } from 'mobx-react';
import { useMidiDeviceSelector } from '@/hooks/useMidiDeviceSelector';
import { useTranslation } from 'react-i18next';
import { Checkbox } from './ui/checkbox';

const midiSteps = [
  { label: 'Выбор устройства' },
  { label: 'Проверка подключения' },
];

const MidiHelper = observer(
  ({
    managerEntity,
    forceOpen,
    onClose,
    isIOS = false,
  }: {
    managerEntity: any;
    forceOpen: boolean;
    onClose: (neverShowAgain: boolean) => void;
    isIOS?: boolean;
  }) => {
    // Use the new MIDI device selector hook
    const {
      selectedDeviceId,
      setSelectedDeviceId,
      midiInputs,
      refreshMidiInputs,
    } = useMidiDeviceSelector(managerEntity);

    const [step, setStep] = useState(0);
    const [testStatus, setTestStatus] = useState<
      'idle' | 'waiting' | 'success'
    >('idle');
    const [dontShowNext, setDontShowNext] = useState(false);
    const { t } = useTranslation();

    // Auto-test MIDI on step 1
    useEffect(() => {
      if (step === 1) {
        setTestStatus('waiting');
        const midiInput =
          managerEntity?.midiManager?.webmidi?._inputs?.[0]?._midiInput;
        if (midiInput) {
          const onMidiMessage = () => {
            setTestStatus('success');
            midiInput.onmidimessage = null;
          };
          midiInput.onmidimessage = onMidiMessage;
        } else {
          setTestStatus('idle');
        }
      }
    }, [step, managerEntity]);

    // Handlers for navigation
    const handleNext = () => {
      if (step < midiSteps.length - 1) setStep(step + 1);
    };
    const handleBack = () => {
      if (step > 0) setStep(step - 1);
    };

    useManagerEffect(() => {
      refreshMidiInputs();
    }, [refreshMidiInputs]);

    useEffect(() => {
      if (!forceOpen) {
        setStep(0);
        setTestStatus('idle');
        // reset other state if needed
      } else {
        refreshMidiInputs();
      }
    }, [forceOpen, refreshMidiInputs]);

    return (
      <Dialog open={forceOpen} onOpenChange={() => onClose(dontShowNext)}>
        <DialogContent className='max-h-[calc(100vh-8rem)] sm:landscape:max-h-[calc(100vh-6rem)] top-[45%] sm:landscape:top-[43%] gap-4 max-md:landscape:gap-1.5 md:gap-4 p-6 sm:landscape:p-3 lg:landscape:p-6'>
          {isIOS ? (
            // iOS-specific content with OrientationSuggestion styling
            <DialogHeader>
              <div className='mx-auto mb-4 h-16 w-16 flex items-center justify-center rounded-full bg-zinc-100'>
                <PiSmileyMehFill className='text-3xl text-zinc-600' />
              </div>
              <DialogTitle className='text-center'>
                {t('midiHelper.ios_not_supported')}
              </DialogTitle>
              <DialogDescription className='text-center'>
                {t('midiHelper.ios_explanation')}
              </DialogDescription>
            </DialogHeader>
          ) : (
            <>
              <DialogHeader>
                <DialogTitle className='text-lg max-lg:landscape:text-sm'>
                  {step === 0
                    ? t('midiHelper.title_connect')
                    : t('midiHelper.check_connection')}
                </DialogTitle>
              </DialogHeader>

              <div className='max-sm:portrait:flex max-sm:portrait:flex-col sm:landscape:grid sm:landscape:grid-cols-2 md:grid md:grid-cols-2 gap-4 sm:landscape:gap-2 items-center'>
                {/* ICON PART - Smaller in landscape */}
                <div className='flex flex-col items-center rounded-md'>
                  {step === 0 && (
                    <div
                      className='flex h-16 w-16 items-center justify-center rounded-full bg-zinc-100 cursor-pointer hover:bg-zinc-200 transition-colors'
                      onClick={refreshMidiInputs}
                    >
                      <PiArrowsClockwiseThin className='text-3xl  text-zinc-600 hover:text-accent-blue' />
                    </div>
                  )}
                  {step === 1 && (
                    <div className='flex flex-col items-center'>
                      {testStatus === 'success' ? (
                        <PiSmileyWinkFill
                          size={64}
                          className='text-accent-blue'
                        />
                      ) : (
                        <PiSmileyMehFill size={64} className='text-zinc-600' />
                      )}
                    </div>
                  )}

                  {/* Don't show next time checkbox */}
                  {!isIOS && (
                    <div className='hidden max-md:landscape:flex items-center mt-1 mb-2'>
                      <Checkbox
                        id='dontShowNext'
                        checked={dontShowNext}
                        onCheckedChange={(checked) =>
                          setDontShowNext(checked === true)
                        }
                        className='mr-2'
                      />
                      <label
                        htmlFor='dontShowNext'
                        className=' text-sm flex items-center'
                      >
                        {t('midiHelper.dont_show_next')}
                      </label>
                    </div>
                  )}
                </div>
                {/* TEXT PART - Larger space in landscape */}
                <div className='flex-1 gap-3 sm:landscape:gap-2 md:gap-3 flex flex-col justify-center'>
                  {step === 0 && (
                    <>
                      <div className='space-y-2 sm:landscape:space-y-1'>
                        <Select
                          value={selectedDeviceId}
                          onValueChange={setSelectedDeviceId}
                        >
                          <SelectTrigger className='w-full min-w-[220px] sm:landscape:min-w-[180px] sm:landscape:h-8'>
                            <SelectValue
                              placeholder={t('midiHelper.no_devices')}
                              className='sm:landscape:text-xs'
                            />
                          </SelectTrigger>
                          <SelectContent
                            fallbackMessage={t('midiHelper.no_devices')}
                          >
                            {midiInputs.map((input: any) => (
                              <SelectItem
                                key={input.id}
                                value={input.id}
                                className='w-full sm:landscape:text-xs'
                              >
                                {input.name}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div className='mb-4 sm:landscape:mb-1 text-xs sm:landscape:text-xs text-black'>
                        {t('midiHelper.connect_and_refresh')}
                      </div>
                    </>
                  )}
                  {step === 1 && (
                    <div className='sm:landscape:mb-2 min-h-[5rem] sm:landscape:min-h-[4rem] flex items-center'>
                      {testStatus === 'success' ? (
                        <div className='flex items-center gap-2 text-green-600 text-sm sm:landscape:text-xs font-semibold'>
                          <CheckCircle2 className='sm:landscape:w-4 sm:landscape:h-4' />
                          <span>{t('midiHelper.device_connected')}</span>
                        </div>
                      ) : (
                        <div className='text-xs sm:landscape:text-xs text-black'>
                          {t('midiHelper.play_any_note')}
                          <br />
                          {t('midiHelper.icon_changes')}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </>
          )}

          {/* Navigation buttons */}
          <div className='flex flex-col md:flex-row justify-between items-start'>
            {/* Don't show next time checkbox */}
            {!isIOS && (
              <div className='flex max-md:landscape:hidden items-center mt-4 max-lg:landscape:mt-0 mb-2'>
                <Checkbox
                  id='dontShowNext'
                  checked={dontShowNext}
                  onCheckedChange={(checked) =>
                    setDontShowNext(checked === true)
                  }
                  className='mr-2'
                />
                <label
                  htmlFor='dontShowNext'
                  className=' text-sm flex items-center'
                >
                  {t('midiHelper.dont_show_next')}
                </label>
              </div>
            )}
            <div
              className={`flex flex-1 gap-2 mt-4 max-lg:landscape:mt-0 flex-row w-full md:w-auto justify-center
                 ${isIOS ? 'justify-center' : 'md:justify-end'}`}
            >
              {isIOS ? (
                <ButtonShadcn
                  variant='blue'
                  onClick={() => onClose(false)}
                  className='flex-1'
                >
                  {t('midiHelper.close')}
                </ButtonShadcn>
              ) : (
                // Normal flow: Back/Next/Close buttons
                <>
                  <ButtonShadcn
                    variant='secondary'
                    onClick={handleBack}
                    disabled={step === 0}
                    className='flex-1 md:flex-initial md:min-w-24 py-1 px-3 sm:landscape:py-1 sm:landscape:px-3'
                  >
                    {t('midiHelper.back')}
                  </ButtonShadcn>
                  {step === 0 ? (
                    <ButtonShadcn
                      variant='blue'
                      onClick={handleNext}
                      disabled={
                        step === midiSteps.length - 1 ||
                        (step === 0 &&
                          (!selectedDeviceId || midiInputs.length === 0))
                      }
                      className='flex-1 md:flex-initial md:min-w-24 py-1 px-3 sm:landscape:py-1 sm:landscape:px-3'
                    >
                      {t('midiHelper.next')}
                    </ButtonShadcn>
                  ) : (
                    <ButtonShadcn
                      variant='blue'
                      onClick={() => onClose(dontShowNext)}
                      className='flex-1 md:flex-initial md:min-w-24 py-1 px-3 sm:landscape:py-1 sm:landscape:px-3'
                    >
                      {t('midiHelper.close')}
                    </ButtonShadcn>
                  )}
                </>
              )}
            </div>
          </div>
        </DialogContent>
      </Dialog>
    );
  }
);

export default MidiHelper;
